import React, { useMemo, useState, useEffect, useCallback } from "react";
import { useTable, usePagination, useSortBy } from "react-table";
import { useAuth } from "../store/Auth";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { base, get_expired_fleet_vehicles, get_expired_single_vehicle, renew_vehicles, get_invoice, states as stateUrl } from "../constants/urls";
import { ClipLoader } from "react-spinners";
import { AiOutlineClose } from "react-icons/ai";
import { useSelectedVehicles } from "../store/SelectedVehiclesStore";
import { useFormStep } from "../store/FormStepStore";
import { formatDate } from "../utils/date_format";
import { useInvoiceStore } from "../store/InvoiceStore";
import Header from "../components/assist/Title";

const vehiclesColumn = (selectedVehicles, setSelectedVehicles, toggleVehicle, searchMode, setShowRenewalModal, handleContinuePayment) => [
    ...(searchMode === "fleet" ? [{
        Header: "Select",
        accessor: "select",
        Cell: ({ row }) => {
            const data = row.original;
            // Disable selection if the vehicle is already Pending
            const isPending = data.cmr_status === "Pending";
            
            return (
                <input
                    type="checkbox"
                    checked={selectedVehicles.includes(data.vehicle_id)}
                    onChange={() => toggleVehicle(data.vehicle_id)}
                    disabled={isPending}
                    className={`h-5 w-5 rounded focus:ring-blue-500 border-gray-300 ${isPending ? 'text-gray-300 cursor-not-allowed opacity-50' : 'text-blue-600 cursor-pointer'}`}
                    aria-label={`Select vehicle ${data.license_number}`}
                    title={isPending ? "Pending vehicles must be continued individually" : "Select for renewal"}
                />
            );
        },
    }] : []),
    {
        Header: "S/No.",
        accessor: "serial",
        Cell: ({ row }) => row.index + 1,
    },
    ...(searchMode === "single" ? [{
        Header: "Owner Name",
        accessor: "owner_name",
        Cell: ({ row }) => {
            const { owner } = row.original;
            return `${owner?.first_name || ''} ${owner?.middle_name || ''} ${owner?.last_name || ''}`.trim();
        },
    }] : []),
    {
        Header: "Licence Number",
        accessor: "license_number",
    },
    {
        Header: "Chassis Number",
        accessor: "chassis_number",
    },
    {
        Header: "Vehicle Make/Model",
        accessor: "make",
    },
    {
        Header: "Color",
        accessor: "color",
    },
    {
        Header: "Usage",
        accessor: "usage",
    },
    {
        Header: "Expiry Date",
        accessor: "expiry_date",
        Cell: ({ row }) => {
            const { cmr_status, expiry_date } = row.original;
            if (cmr_status === "Pending") {
                return <span className="text-gray-400 font-medium">-</span>;
            }
            return expiry_date ? formatDate(expiry_date) : "N/A";
        },
    },
    {
        Header: "Vehicle Status",
        accessor: "vehicle_status",
        Cell: ({ row }) => {
            const { vehicle_status } = row.original;
            const statusColor = vehicle_status === "Valid" || vehicle_status === "Active" ? "text-green-600" :
                              vehicle_status === "Pending" ? "text-orange-600" : "text-red-600";
            return (
                <span className={`font-medium ${statusColor}`}>
                    {vehicle_status === "Expired" ? "Expired" : vehicle_status}
                </span>
            );
        },
    },
    {
        Header: "CMR Status",
        accessor: "cmr_status",
        Cell: ({ row }) => {
            const { cmr_status, payment_status } = row.original;
            const statusColor = cmr_status === "Approved" ? "text-green-600" :
                              cmr_status === "Pending" ? "text-orange-600" : "text-red-600";
            return (
                <span className={`font-medium ${statusColor}`}>
                    {payment_status === "Unpaid" ? "Incomplete" : cmr_status}
                </span>
            );
        },
    },
    // We add an individual action column in Fleet mode specifically for the "Continue" button
    ...(searchMode === "fleet" ? [{
        Header: "Action",
        accessor: "action",
        Cell: ({ row }) => {
            const data = row.original;
            const { cmr_status } = data;
            
            if (cmr_status !== "Pending") {
                return null; // Expired/Approved vehicles are handled via the batch checkbox in fleet mode
            }

            return (
                <button 
                    onClick={() => handleContinuePayment(data)}
                    className="px-4 py-1.5 bg-orange-500 hover:bg-orange-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 transition-colors text-sm w-full sm:w-auto"
                    aria-label={`Continue vehicle ${data.license_number}`}
                >
                    Continue
                </button>
            );
        },
    }] : []),
    ...(searchMode === "single" ? [{
        Header: "Action",
        accessor: "action",
        Cell: ({ row }) => {
            const data = row.original;
            const { cmr_status } = data;
            
            const handleAction = () => {
                if (cmr_status === "Pending") {
                    handleContinuePayment(data);
                } else {
                    setSelectedVehicles([data.vehicle_id]);
                    setShowRenewalModal(true);
                }
            };

            let buttonText = "Renew";
            let btnClass = "bg-blue-600 hover:bg-blue-700 focus:ring-blue-500 text-white";

            if (cmr_status === "Pending") {
                buttonText = "Continue";
                btnClass = "bg-orange-500 hover:bg-orange-600 focus:ring-orange-500 text-white";
            } else if (cmr_status === "Approved") {
                buttonText = "Renew Now";
                btnClass = "bg-red-600 hover:bg-red-700 focus:ring-red-500 text-white";
            }

            return (
                <button 
                    onClick={handleAction}
                    className={`px-4 py-2 rounded-lg focus:outline-none focus:ring-2 transition-colors w-full sm:w-auto ${btnClass}`}
                    aria-label={`${buttonText} vehicle ${data.license_number}`}
                >
                    {buttonText}
                </button>
            );
        },
    }] : []),
];

export default function VehicleSearch() {
    const [result, setResult] = useState([]);
    const [loading, setLoading] = useState(false);
    const [idNumber, setIdNumber] = useState("");
    const [licenseChassisNumber, setLicenseChassisNumber] = useState("");
    const [searchType, setSearchType] = useState("id");
    const { auth: { token } } = useAuth();
    const [userId, setUserId] = useState(null);
    const { selectedVehicles, setSelectedVehicles, toggleVehicle, clearSelectedVehicles } = useSelectedVehicles();
    const { setStep } = useFormStep();
    const { setInvoiceDetails } = useInvoiceStore();
    const [showRenewalModal, setShowRenewalModal] = useState(false);
    const [selectedState, setSelectedState] = useState("");
    const [states, setStates] = useState([]);
    const [proceeding, setProceeding] = useState(false);
    const [searchMode, setSearchMode] = useState("");
    const [ownerName, setOwnerName] = useState("");
    const [vehiclesCount, setVehiclesCount] = useState("");
    const navigate = useNavigate();

    const handleSearchModeChange = (mode) => {
        setSearchMode(mode);
        setResult([]);
        setIdNumber("");
        setLicenseChassisNumber("");
        setOwnerName("");
        clearSelectedVehicles();
        setSelectedState("");
        setSearchType(mode === "single" ? "vehicle" : "id");
    };

    const data = useMemo(() => {
        return result.map(vehicle => ({
            ...vehicle,
            isSelected: selectedVehicles.includes(vehicle.vehicle_id)
        }));
    }, [result, selectedVehicles]);

    // Calculate how many vehicles are actually eligible for batch selection
    const renewableVehiclesCount = useMemo(() => {
        return result.filter(v => v.cmr_status !== "Pending").length;
    }, [result]);

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            // Only select vehicles that are NOT pending
            const eligibleIds = result
                .filter(vehicle => vehicle.cmr_status !== "Pending")
                .map(vehicle => vehicle.vehicle_id);
            setSelectedVehicles(eligibleIds);
        } else {
            clearSelectedVehicles();
        }
    };

    const handleRenewal = () => {
        if (selectedVehicles.length === 0) {
            Swal.fire("Please select at least one vehicle to process");
            return;
        }
        setShowRenewalModal(true);
    };

    const handleContinuePayment = useCallback(async (vehicleData) => {
        if (!vehicleData.cmr_reference) {
            Swal.fire("Error", "No transaction reference found for this vehicle.", "error");
            return;
        }

        setLoading(true);
        try {
            if (!token) throw new Error("Authentication token is missing");

            const url = `${base}${get_invoice}${vehicleData.cmr_reference}`; 

            const response = await fetch(url, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                }
            });

            const result = await response.json();

            if (result.statusCode == 0) {
                let invoiceData = result.data.transaction;
                
                setInvoiceDetails({
                    firstName: invoiceData.first_name || '',
                    middleName: invoiceData.middle_name || '',
                    lastName: invoiceData.last_name || '',
                    address: invoiceData.address || '',
                    email: invoiceData.email || '',
                    phone: invoiceData.phone || '',
                    narration: invoiceData.narration || '',
                    amount: invoiceData.amount || '0',
                    rrr: invoiceData.rrr || '',
                    transactionId: invoiceData.trans_ref || '',
                    invoiceNumber: invoiceData.invoice_number || '',
                    initiatedDate: invoiceData.initiate_date || '',
                    status: "Unpaid",
                    productCode: invoiceData.product_code || '',
                    make: invoiceData.make || '',
                    vin: invoiceData.vin || '',
                    licenceNumber: invoiceData.license || '',
                    vat: invoiceData.vat || '0',
                    product: invoiceData.product || '',
                    requestItems: invoiceData.request_items || [],
                    numberOfEntry: invoiceData.number_of_entry || '0'
                });

                clearSelectedVehicles();
                setStep(6);
                navigate("/invoice"); 
            } else {
                throw new Error(result.message || "Unable to retrieve transaction");
            }
        } catch (err) {
            Swal.fire({
                title: "Error",
                text: err.message || "Unable to continue, please try again.",
                icon: "error",
                confirmButtonText: "Ok",
                confirmButtonColor: "#1C89DE",
            });
        } finally {
            setLoading(false);
        }
    }, [token, setInvoiceDetails, clearSelectedVehicles, setStep, navigate]);

    const processRenewal = useCallback(async () => {
        if (!selectedState) {
            Swal.fire("Please select a requesting state");
            return;
        }
        setProceeding(true);
        try {
            if (!token) {
                throw new Error("Authentication token is missing");
            }

            const url = base + renew_vehicles;
            const payload = {
                vehicle_ids: selectedVehicles,
                user_id: parseInt(userId),
                reference: `${Date.now()}${Math.random().toString(36).substr(2, 6).toUpperCase()}`,
                request_state: selectedState
            };

            const response = await fetch(url, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(payload)
            });

            const responseData = await response.json();

            if (responseData.statusCode === 0 && responseData.data?.invoice) {
                setInvoiceDetails({
                    firstName: responseData.data.invoice.first_name || '',
                    middleName: responseData.data.invoice.middle_name || '',
                    lastName: responseData.data.invoice.last_name || '',
                    address: responseData.data.invoice.address || '',
                    email: responseData.data.invoice.email || '',
                    phone: responseData.data.invoice.phone || '',
                    narration: responseData.data.invoice.narration || '',
                    amount: responseData.data.invoice.amount || '0',
                    rrr: responseData.data.invoice.rrr || '',
                    transactionId: responseData.data.invoice.trans_ref || '',
                    invoiceNumber: responseData.data.invoice.invoice_number || '',
                    initiatedDate: formatDate(responseData.data.invoice.initiated_date) || '',
                    status: responseData.data.invoice.status || 'Pending',
                    productCode: responseData.data.invoice.product_code || '',
                    make: responseData.data.invoice.make || '',
                    vin: responseData.data.invoice.vin || '',
                    licenceNumber: responseData.data.invoice.Licence || '',
                    vat: responseData.data.invoice.vat || '0',
                    product: responseData.data.invoice.product || '',
                    requestItems: responseData.data.request_items || [],
                    numberOfEntry: responseData.data.number_of_entry || '0'
                });

                clearSelectedVehicles();
                setStep(6);
                setShowRenewalModal(false);
                navigate("/invoice"); 
            } else {
                throw new Error(responseData.message || "Failed to process request");
            }
        } catch (err) {
            setProceeding(false);
            Swal.fire({
                title: "Error",
                text: err.message || "Error processing request. Please try again.",
                icon: "error",
                confirmButtonText: "Ok",
                confirmButtonColor: "#1C89DE",
            });
        }
    }, [selectedState, selectedVehicles, userId, token, setInvoiceDetails, clearSelectedVehicles, setStep, navigate]);

    const columns = useMemo(
        () => vehiclesColumn(selectedVehicles, setSelectedVehicles, toggleVehicle, searchMode, setShowRenewalModal, handleContinuePayment),
        [selectedVehicles, setSelectedVehicles, toggleVehicle, searchMode, setShowRenewalModal, handleContinuePayment]
    );

    const handleSearch = async (e) => {
        e.preventDefault();
        if (!idNumber.trim() && !licenseChassisNumber.trim()) {
            Swal.fire(`Please enter a ${searchType === "id" ? "ID number" : "License/Chassis number"}`);
            return;
        }

        setLoading(true);
        try {
            const endpoint = searchType === "id" 
                ? `${base}${get_expired_fleet_vehicles}/${idNumber}`
                : `${base}${get_expired_single_vehicle}/${licenseChassisNumber}`;
            
            const response = await fetch(endpoint, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
            });

            const responseData = await response.json();
            
            const safeVehicles = Array.isArray(responseData.data?.vehicles) ? responseData.data.vehicles : [];
            console.log(safeVehicles);
            setUserId(responseData.data?.owner?.id);
            setVehiclesCount(safeVehicles.length);
            setOwnerName(`${responseData.data?.owner?.first_name || ''} ${responseData.data?.owner?.middle_name || ''} ${responseData.data?.owner?.last_name || ''}`.trim());
            
            const vehiclesWithIds = safeVehicles.map((vehicle) => ({
                ...vehicle,
                id: vehicle.id,
                owner: responseData.data.owner
            }));

            clearSelectedVehicles();
            setResult(vehiclesWithIds);
        } catch (err) {
            Swal.fire(err.response?.data?.message || "Unable to fetch vehicles");
            setResult([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const fetchStates = async () => {
            try {
                const response = await fetch(`${base}${stateUrl}`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                const responseData = await response.json();
                setStates(responseData.data || []);
            } catch (err) {
                console.error("Error fetching states:", err);
            }
        };
        fetchStates();
    }, [token]);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        page,
        prepareRow,
        nextPage,
        previousPage,
        canPreviousPage,
        pageOptions,
        canNextPage,
        setPageSize,
        gotoPage,
        state: { pageIndex, pageSize },
    } = useTable(
        {
            columns,
            data,
            initialState: { pageSize: 10 },
        },
        useSortBy,
        usePagination
    );

    return (
        <div className="min-h-screen bg-gray-100">
            <Header />
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
                    <div className="flex gap-2">
                        <button
                            onClick={() => handleSearchModeChange("single")}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                                searchMode === "single"
                                    ? "bg-blue-600 text-white"
                                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                            }`}
                            aria-label="Switch to single vehicle search"
                        >
                            Single
                        </button>
                        <button
                            onClick={() => handleSearchModeChange("fleet")}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                                searchMode === "fleet"
                                    ? "bg-blue-600 text-white"
                                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                            }`}
                            aria-label="Switch to fleet vehicle search"
                        >
                            Fleet
                        </button>
                    </div>
                    {searchMode === "fleet" && (
                        <button
                            onClick={handleRenewal}
                            disabled={selectedVehicles.length === 0}
                            className={`px-6 py-2 rounded-lg text-sm font-medium transition-colors ${
                                selectedVehicles.length > 0
                                    ? "bg-green-600 text-white hover:bg-green-700"
                                    : "bg-gray-400 text-white cursor-not-allowed"
                            }`}
                            aria-label={`Renew ${selectedVehicles.length} vehicles`}
                        >
                            Renew Selected {selectedVehicles.length > 0 && `(${selectedVehicles.length})`}
                        </button>
                    )}
                </div>

                {searchMode && (
                    <div className="mb-6">
                        <form onSubmit={handleSearch} className="flex gap-3 items-center">
                            <input
                                type="text"
                                value={searchType === "id" ? idNumber : licenseChassisNumber}
                                onChange={(e) => {
                                    if (searchType === "id") {
                                        setIdNumber(e.target.value);
                                    } else {
                                        setLicenseChassisNumber(e.target.value);
                                    }
                                }}
                                placeholder={searchMode === "single" ? "Licence/Chassis No." : "ID Number"}
                                className="w-full sm:w-64 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                aria-label="Search input"
                            />
                            <button
                                type="submit"
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                                aria-label="Search vehicles"
                            >
                                Search
                            </button>
                        </form>
                    </div>
                )}

                {searchMode !== "single" && ownerName && (
                    <div className="mb-6">
                        <p className="text-gray-700">
                            <span className="font-medium">Owner's Name:</span>{" "}
                            <span className="font-bold">{ownerName.toUpperCase()}</span>
                        </p>
                    </div>
                )}

                {/* Desktop Table View */}
                <div className="hidden md:block overflow-x-auto bg-white rounded-lg shadow">
                    <table {...getTableProps()} className="w-full">
                        <thead>
                            {headerGroups.map((headerGroup) => (
                                <tr {...headerGroup.getHeaderGroupProps()} className="bg-blue-100">
                                    {headerGroup.headers.map((column) => (
                                        <th
                                            {...column.getHeaderProps(column.getSortByToggleProps())}
                                            className="py-3 px-4 text-left text-sm font-semibold text-gray-600"
                                        >
                                            {column.id === "select" ? (
                                                <input
                                                    type="checkbox"
                                                    checked={selectedVehicles.length === renewableVehiclesCount && renewableVehiclesCount > 0}
                                                    onChange={handleSelectAll}
                                                    disabled={renewableVehiclesCount === 0}
                                                    className="h-5 w-5 text-blue-600 rounded focus:ring-blue-500 border-gray-300 disabled:opacity-50"
                                                    aria-label="Select all renewable vehicles"
                                                />
                                            ) : (
                                                <>
                                                    {column.render("Header")}
                                                    <span className="ml-1">
                                                        {column.isSorted ? (column.isSortedDesc ? "↓" : "↑") : ""}
                                                    </span>
                                                </>
                                            )}
                                        </th>
                                    ))}
                                </tr>
                            ))}
                        </thead>
                        <tbody {...getTableBodyProps()}>
                            {loading ? (
                                <tr>
                                    <td colSpan={headerGroups[0].headers.length} className="text-center py-8">
                                        <ClipLoader color="#166434" size={30} />
                                        <p className="mt-2 text-gray-500">Loading...</p>
                                    </td>
                                </tr>
                            ) : result.length === 0 ? (
                                <tr>
                                    <td colSpan={headerGroups[0].headers.length} className="text-center py-8 text-gray-500">
                                        No vehicles found
                                    </td>
                                </tr>
                            ) : (
                                page.map((row) => {
                                    prepareRow(row);
                                    return (
                                        <tr {...row.getRowProps()} className="border-t hover:bg-gray-50">
                                            {row.cells.map((cell) => (
                                                <td {...cell.getCellProps()} className="py-3 px-4">
                                                    {cell.render("Cell")}
                                                </td>
                                            ))}
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Mobile Card View */}
                <div className="md:hidden space-y-4">
                    {loading ? (
                        <div className="flex flex-col items-center py-8">
                            <ClipLoader color="#166434" size={30} />
                            <p className="mt-2 text-gray-500">Loading...</p>
                        </div>
                    ) : result.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                            No vehicles found
                        </div>
                    ) : (
                        page.map((row) => {
                            prepareRow(row);
                            return (
                                <div key={row.id} className="bg-white p-4 rounded-lg shadow">
                                    {row.cells.map((cell) => {
                                        if (cell.column.id === "select" || cell.column.id === "action") {
                                            return (
                                                <div key={cell.column.id} className="flex justify-between items-center py-2">
                                                    {cell.render("Cell")}
                                                </div>
                                            );
                                        }
                                        if (cell.column.id === "serial") return null;
                                        return (
                                            <div key={cell.column.id} className="py-1">
                                                <span className="font-medium text-gray-600">{cell.column.Header}:</span>{" "}
                                                {cell.render("Cell")}
                                            </div>
                                        );
                                    })}
                                </div>
                            );
                        })
                    )}
                </div>

                {result.length > 0 && (
                    <div className="flex flex-col sm:flex-row justify-between items-center mt-6 gap-4">
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => gotoPage(0)}
                                disabled={!canPreviousPage}
                                className="px-3 py-1 border rounded disabled:opacity-50 text-sm"
                                aria-label="Go to first page"
                            >
                                {"<<"}
                            </button>
                            <button
                                onClick={() => previousPage()}
                                disabled={!canPreviousPage}
                                className="px-3 py-1 border rounded disabled:opacity-50 text-sm"
                                aria-label="Previous page"
                            >
                                Previous
                            </button>
                            <button
                                onClick={() => nextPage()}
                                disabled={!canNextPage}
                                className="px-3 py-1 border rounded disabled:opacity-50 text-sm"
                                aria-label="Next page"
                            >
                                Next
                            </button>
                            <button
                                onClick={() => gotoPage(pageOptions.length - 1)}
                                disabled={!canNextPage}
                                className="px-3 py-1 border rounded disabled:opacity-50 text-sm"
                                aria-label="Go to last page"
                            >
                                {">>"}
                            </button>
                            <span className="text-sm">
                                Page{" "}
                                <strong>
                                    {pageIndex + 1} of {pageOptions.length} 
                                </strong>
                            </span>

                            <span className="text-sm">
                                | No of Records{" "}
                                <strong>
                                    {vehiclesCount} Requests 
                                </strong>
                            </span>
                        </div>
                        <select
                            value={pageSize}
                            onChange={(e) => setPageSize(Number(e.target.value))}
                            className="px-3 py-1 border rounded text-sm"
                            aria-label="Select page size"
                        >
                            {[10, 20, 30, 40, 50].map((size) => (
                                <option key={size} value={size}>
                                    Show {size}
                                </option>
                            ))}
                        </select>
                    </div>
                )}

                {showRenewalModal && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white p-6 rounded-lg w-full max-w-md">
                            <div className="flex justify-between items-center mb-4">
                                <h2 className="text-xl font-bold">Select Requesting State</h2>
                                <button 
                                    onClick={() => setShowRenewalModal(false)}
                                    className="text-gray-500 hover:text-gray-700"
                                    aria-label="Close modal"
                                >
                                    <AiOutlineClose size={24} />
                                </button>
                            </div>
                            <select
                                value={selectedState}
                                onChange={(e) => setSelectedState(e.target.value)}
                                className="w-full p-2 border rounded mb-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                aria-label="Select requesting state"
                            >
                                <option value="">Select a state</option>
                                {states.map((state, index) => (
                                    <option key={index} value={state}>
                                        {state}
                                    </option>
                                ))}
                            </select>
                            <div className="flex justify-end gap-2">
                                <button
                                    onClick={() => setShowRenewalModal(false)}
                                    className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
                                    aria-label="Cancel processing"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={processRenewal}
                                    disabled={proceeding}
                                    className={`px-4 py-2 rounded text-white transition-colors ${
                                        proceeding ? "bg-gray-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"
                                    }`}
                                    aria-label="Proceed to processing"
                                >
                                    {proceeding ? "Processing..." : "Proceed"}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}